import { readLocaleCookie } from "./cookie";
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  normalizeLocale,
  type Locale,
} from "./locales";

interface ResolveOptions {
  /** From `member_profiles/{uid}.locale` if signed in. */
  profileLocale?: string | null;
  /**
   * Browser/system locale, typically `navigator.language`. Pass-through so this
   * function stays pure and SSR-safe.
   */
  navigatorLanguage?: string | null;
}

export interface ResolvedLocale {
  locale: Locale;
  source: "profile" | "cookie" | "navigator" | "default";
}

/**
 * Resolution order:
 *   1. profile.locale (signed-in, persisted across devices)
 *   2. zoey_locale cookie (signed-out, per-device)
 *   3. navigator.language (first-launch best guess)
 *   4. DEFAULT_LOCALE
 */
export function resolveLocale(opts: ResolveOptions = {}): ResolvedLocale {
  if (isSupportedLocale(opts.profileLocale)) {
    return { locale: opts.profileLocale, source: "profile" };
  }
  const cookie = readLocaleCookie();
  if (cookie) return { locale: cookie, source: "cookie" };
  if (opts.navigatorLanguage) {
    const normalized = normalizeLocale(opts.navigatorLanguage);
    if (normalized !== DEFAULT_LOCALE || opts.navigatorLanguage.startsWith("en")) {
      return { locale: normalized, source: "navigator" };
    }
  }
  return { locale: DEFAULT_LOCALE, source: "default" };
}
